import React, { useEffect, useRef } from 'react';
import { Input } from '../../../../../../base/components/Forms/Input';
import { RadioSelectWithContent } from '../../../../../../base/components/Forms/RadioSelectWithContent';
import { Icon } from '../../../../../../base/components/Icon';
import { useAppDispatch, useAppSelector } from '../../../../../../base/hooks';
import { getRefById, RefMap, scrollToError } from '../../../../../../base/utils';
import { __t } from '../../../../../../base/utils/t';
import { signUpActions } from '../../../redux/slices/signUpSlice';
import { ClickTooltip } from '../../../../../../base/components/ClickTooltip';
import { RecruiterType } from '../../../../../enums';
import { CityAutocomplete } from '../../../../../../base/components/CityAutocomplete';
import { City } from '../../../../../../base/interfaces/config';
import { recruiterAppActions } from '../../../../../redux/slices/recruiterAppSlice';

import '../styles.scss';

export const CompanyInfo = () => {
    const dispatch = useAppDispatch();
    const {
        fields: { recruiterType, agencyName, city },
        errors,
        shouldScrollToError,
    } = useAppSelector((state) => state.recruiterAppSignUp);
    const { citySearchOptions } = useAppSelector((state) => state.recruiterApp);

    const refMap: RefMap[] = [
        {
            id: 'agencyName',
            ref: useRef<HTMLDivElement>(null),
        },
        {
            id: 'city',
            ref: useRef<HTMLDivElement>(null),
        },
    ];

    useEffect(() => {
        if (errors && shouldScrollToError) {
            scrollToError(errors, refMap);
            dispatch(signUpActions.setShouldScrollToError(false));
        }
    }, [refMap, errors, shouldScrollToError]);

    const inputChangeHandler = (id: string, value: unknown) => {
        dispatch(signUpActions.setField({ id, value }));
    };

    return (
        <div className='recruiter-signup__form-wrapper'>
            <div className='recruiter-signup__form-item'>
                <p className='recruiter-signup__form-label'>{__t('Кем вы являетесь?')}</p>
                <fieldset className='recruiter-signup__radio-select-fieldset'>
                    <RadioSelectWithContent
                        id='recruiterType'
                        value={RecruiterType.Agency}
                        checked={recruiterType === RecruiterType.Agency}
                        onChange={inputChangeHandler}
                        className='recruiter-signup__radio-select-item'
                    >
                        <div className='recruiter-signup__radio-select-item-content'>
                            <Icon icon='building-thin' size='40' className='recruiter-signup__radio-select-icon' />
                            <span className='recruiter-signup__radio-select-text'>{__t('Агентство')}</span>
                        </div>
                    </RadioSelectWithContent>
                    <RadioSelectWithContent
                        id='recruiterType'
                        value={RecruiterType.Individual}
                        checked={recruiterType === RecruiterType.Individual}
                        onChange={inputChangeHandler}
                        className='recruiter-signup__radio-select-item'
                    >
                        <div className='recruiter-signup__radio-select-item-content'>
                            <Icon icon='user-tie-thin' size='40' className='recruiter-signup__radio-select-icon' />
                            <span className='recruiter-signup__radio-select-text'>{__t('Частный рекрутер')}</span>
                        </div>
                    </RadioSelectWithContent>
                </fieldset>
            </div>

            {recruiterType === RecruiterType.Agency && (
                <div
                    className='recruiter-signup__form-item scroll-margin-for-fixed-header'
                    ref={getRefById('agencyName', refMap)?.ref}
                >
                    <p className='recruiter-signup__form-label'>{__t('Название компании')}</p>
                    <Input
                        value={agencyName}
                        placeholder={__t('Введите название')}
                        onChange={(value: string) => inputChangeHandler('agencyName', value)}
                        errors={errors?.agencyName}
                    />
                </div>
            )}

            {recruiterType === RecruiterType.Individual && (
                <div
                    className='recruiter-signup__form-item scroll-margin-for-fixed-header'
                    ref={getRefById('agencyName', refMap)?.ref}
                >
                    <p className='recruiter-signup__form-label'>{__t('Имя и фамилия')}</p>
                    <ClickTooltip
                        activeButton={
                            <Input
                                value={agencyName}
                                placeholder={__t('Введите ваше имя')}
                                onChange={(value: string) => inputChangeHandler('agencyName', value)}
                                errors={errors?.agencyName}
                            />
                        }
                        align='right'
                        className='recruiter-signup__input-tooltip-container'
                        hideCloseIcon={true}
                    >
                        <div className='recruiter-signup__input-tooltip'>
                            <p className='recruiter-signup__input-tooltip-title'>
                                {__t('Важно указать корректные данные')}
                            </p>{' '}
                            <p className='recruiter-signup__input-tooltip-text'>
                                {__t('После регистрации мы проверяем  аккаунт на достоверность данных.')}
                            </p>
                        </div>
                    </ClickTooltip>
                </div>
            )}

            <div
                className='recruiter-signup__form-item scroll-margin-for-fixed-header'
                ref={getRefById('name', refMap)?.ref}
            >
                <p className='recruiter-signup__form-label'>{__t('В каком городе находитесь?')}</p>
                <CityAutocomplete
                    appType='worker'
                    options={citySearchOptions}
                    selectedOption={city}
                    errors={errors?.cityId}
                    onSearch={(value: string) => dispatch(recruiterAppActions.citySearch(value))}
                    setSearchOptions={(options: City[] | null) => dispatch(recruiterAppActions.setCitySearch(options))}
                    onSelect={(value: City | null) =>
                        dispatch(signUpActions.setField({ id: 'city', value, errorGroupName: 'cityId' }))
                    }
                />
            </div>
        </div>
    );
};
